import {useCompanyTariffGuard} from '../../../../../modules/company-tariff';
import {FC, useContext, useState} from 'react';
import {useForm, SubmitHandler, Controller} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {useRecoilState, useSetRecoilState} from 'recoil';
import {toast} from 'react-toastify';

import Stack from '@mui/material/Stack/Stack';
import Divider from '@mui/material/Divider/Divider';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ContactPersonForm from './ContactPersonForm/ContactPersonForm';
import LoadingButton from '@mui/lab/LoadingButton';
import ControlledInput from '../../../../../ui/controlled-input/ControlledInput';

import {ICompanyFormInputs} from '../../../../../shared/types/company.types';
import {CompanyService} from '../../../../../api/services/company.service';
import {PersonService} from '../../../../../api/services/person.service';

import {contactPersonsToSubmitState} from '../../../../../store/contact-person/atoms';
import {companiesQueryTrigger} from '../../../../../store/company/atoms';

import {PartyRelationsService} from '../../../../../api/services/partyRelations.service';

import {validateInn} from '../../../../../shared/validation/innValidation';
import {validateOrgnOgnip} from '../../../../../shared/validation/ogrnValidation';
import {EventBusContext} from '../../../../../providers/EventBus';

interface IAddCompanyFormInputs extends ICompanyFormInputs {
  addContactPerson: boolean;
}

const AddCompanyForm: FC = () => {
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const [contactPersonsToSubmit, setContactPersonsToSubmit] = useRecoilState(contactPersonsToSubmitState);
  const triggerCompaniesQuery = useSetRecoilState(companiesQueryTrigger);
  const {handleSubmit, control, trigger, watch, reset} = useForm<IAddCompanyFormInputs>({
    mode: 'onChange',
  });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const {bus} = useContext(EventBusContext);

  const watchAddContactPerson = watch('addContactPerson', false);

  const onSubmit: SubmitHandler<ICompanyFormInputs> = async (data) => {
    setLoading(true);

    try {
      const {data: createdCompany} = await CompanyService.create({
        fullCompanyName: data.fullCompanyName.trim(),
        inn: data.inn,
        ogrn: data.ogrn || null,
        description: data.description || null,
        website: data.website || null,
      });

      contactPersonsToSubmit.length &&
        Promise.all(
          contactPersonsToSubmit.map(async (person) => {
            const {data: createdPerson} = await PersonService.create({
              firstName: person.firstName,
              lastName: person.lastName,
              patronymic: person.patronymic || null,
              phoneNumber: person.phoneNumber,
              phoneNumber2: null,
              email: person.email,
              birthDate: person.birthDate || null,
            });

            await PartyRelationsService.create({
              relationTitle: person.relationTitle,
              description: person.description,
              relationType: 'CONTACT_PERSON',
              fromId: createdPerson.id,
              toId: createdCompany.id,
            });
          }),
        );

      toast.success('Компания добавлена', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      setTimeout(() => {
        setLoading(false);
        setContactPersonsToSubmit([]);
        triggerCompaniesQuery((v) => v + 1);
        reset();
        navigate('/customers');
      }, 1500);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (checkTariff()) void handleSubmit(onSubmit)(event);
      }}
    >
      <Stack direction="column" spacing={2}>
        <ControlledInput
          control={control}
          rules={{
            required: 'Обязательно для заполнения',
            maxLength: {
              value: 255,
              message: 'Максимум 255 символов',
            },
          }}
          disabled={loading}
          name="fullCompanyName"
          label="Полное название компании"
          data-testid="fullCompanyName"
        />
        <Stack direction="row" spacing={2}>
          <ControlledInput
            control={control}
            name="inn"
            rules={{
              required: 'Обязательно для заполнения',
              pattern: {
                value: /^(\d{10})?(\d{12})?$/,
                message: '10 или 12 цифр',
              },
              validate: {
                validateInn: (value, formValues) => validateInn(value, formValues.ogrn),
              },
            }}
            label="ИНН"
            size="small"
            disabled={loading}
            data-testid="inn"
          />
          <ControlledInput
            control={control}
            name="ogrn"
            rules={{
              pattern: {
                value: /^(\d{13})?(\d{15})?$/,
                message: '13 или 15 цифр',
              },

              validate: {
                validateOgrnOgrnip: (value, formValues) => validateOrgnOgnip(value, formValues.inn),
              },
            }}
            label="OГРН/ОГРНИП"
            size="small"
            disabled={loading}
            data-testid="ogrn"
          />
          <ControlledInput
            control={control}
            name="website"
            label="Сайт компании"
            size="small"
            disabled={loading}
            sx={{flexGrow: 2}}
            data-testid="website"
          />
        </Stack>
        <ControlledInput
          control={control}
          name="description"
          rules={{
            maxLength: {
              value: 400,
              message: 'Максимум 400 символов',
            },
          }}
          disabled={loading}
          label="Комментарий"
          multiline
          additionalFunc={() => {
            trigger('description');
          }}
          data-testid="description"
        />
        <Stack direction="row">
          <Controller
            control={control}
            name="addContactPerson"
            render={({field: {onChange, value, ref}}) => (
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => {
                      if (contactPersonsToSubmit.length) return;

                      onChange(e);
                    }}
                    checked={value || !!contactPersonsToSubmit.length}
                    inputRef={ref}
                    data-testid="checkbox"
                  />
                }
                label="Контактные лица"
              />
            )}
          />
        </Stack>
        {watchAddContactPerson && (
          <>
            <Divider />
            <ContactPersonForm />
          </>
        )}
        <Stack direction="row" sx={{justifyContent: 'space-between'}}>
          <Button onClick={handleCancel} color="error" variant="outlined" disabled={loading}>
            Отменить
          </Button>
          <LoadingButton
            color="success"
            data-testid="add"
            disabled={tariffLoading}
            type="submit"
            loading={loading}
            variant="outlined"
          >
            Добавить в систему
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};

export default AddCompanyForm;
